/**
 * Monitor JWT access-token storage.
 *
 * The access token is intentionally NOT persisted in localStorage or in
 * a JS-readable cookie: keeping it in sessionStorage + an in-memory
 * cache scopes its lifetime to the tab and keeps it out of XSS-readable
 * persistent storage. The refresh token is held server-side as an
 * httpOnly cookie (set by `ExchangeLogtoToken` / `LogIn` / `RefreshMonitorToken`)
 * and never reaches JavaScript.
 *
 * sessionStorage rather than memory-only because monitor-ui's auth
 * boot order routes the Apollo authLink before any React state has
 * hydrated; we need the token survivable across the initial page load
 * after sign-in, but it should evaporate when the tab closes.
 */

const STORAGE_KEY = "AUTH_TOKEN";

let inMemoryToken = null;

export function getAuthToken() {
  if (inMemoryToken !== null) return inMemoryToken;
  try {
    inMemoryToken = sessionStorage.getItem(STORAGE_KEY);
  } catch {
    inMemoryToken = null;
  }
  return inMemoryToken;
}

export function setAuthToken(token) {
  inMemoryToken = token ?? null;
  try {
    if (token) {
      sessionStorage.setItem(STORAGE_KEY, token);
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Storage may be unavailable (private mode, quota, etc.). The
    // in-memory copy is enough to keep the current session working;
    // the user just has to re-authenticate after a reload.
  }
}

export function clearAuthToken() {
  inMemoryToken = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Same rationale as setAuthToken.
  }
}
